import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { makePrioStyles } from '../../../theme/utils';
import GridLayout, { Layout } from 'react-grid-layout';
import DashboardBaseItem from './DashboardBaseItem';
import { DashboardItem, DashboardWidgetType } from '../../../models/Dashboard';
import { v4 as uuid } from 'uuid';
import * as ConfigValues from '../../../util/configValues';
import { useSelector } from 'react-redux';
import {
  getProject,
  getUserMe,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import {
  apiCreateDashboard,
  apiCreateDashboardWidget,
  apiCreateProjectDashboard,
  apiCreateProjectDashboardWidget,
  apiDeleteDashboard,
  apiDeleteDashboardWidget,
  apiDeleteProjectDashboard,
  apiDeleteProjectDashboardWidget,
  apiFetchDashboard,
  apiUpdateDashboardWidget,
  apiUpdateProjectDashboard,
  apiUpdateProjectDashboardWidget,
} from '../api';
import { dashboardItemToWidgetDto, widgetDtoToDashboardItem } from './util';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import WidgetActions from './WidgetActions';
import DashboardActionsBar from './DashboardActionsBar';
import { Button, Drawer, useTheme } from '@prio365/prio365-react-library';
import WidgetCard from './WidgetCard';
import { Project } from '../../../models/Project';
import Flex from '../../../components/Flex';
import { PrioTheme } from '../../../theme/types';
import DashboardEditWidgetLinkContentDrawer, {
  DashboardEditWidgetLinkContentDrawerHandle,
} from './DashboardEditWidgetLinkContentDrawer';
import DashboardContext from '../context/dashboardContext';
import DashboardEditWidgetPowerBIContentDrawer, {
  DashboardEditWidgetPowerBIContentDrawerHandle,
} from './DashboardEditWidgetPowerBIContentDrawer';
import { isProjectAdmin } from '../../projects/utils';
import {
  DefaultDashboardSetting,
  SettingKey,
  UpdateSetting,
} from '../../../models/Settings';
import {
  apiCreateSetting,
  apiFetchSettings,
  apiUpdateSetting,
} from '../../settings/api';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    overflow: 'scroll',
    paddingBottom: theme.old.spacing.unit(8),
    backgroundColor: theme.old.palette.backgroundPalette.sub,
    '& .react-grid-placeholder': {
      backgroundColor: theme.old.palette.backgroundPalette.active.content,
    },
    '& .react-grid-item': {
      backgroundColor: theme.old.palette.backgroundPalette.content,
      boxShadow: theme.old.palette.boxShadow.regular,
      '& > .react-resizable-handle': {
        visibility: 'hidden',
      },
      '&:hover > .react-resizable-handle': {
        visibility: 'visible',
      },
    },
  },
  widgetItemActions: {
    position: 'relative',
    top: '50%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.old.spacing.unit(1),
    visibility: 'hidden',
    opacity: 0,
    borderRadius: '0 0 0 5px',
    '& button': {
      minWidth: 'auto',
      padding: '4px 8px',
    },
  },
  widgetItem: {
    padding: theme.old.spacing.unit(2),
    overflow: 'visible',
    zIndex: 0,
  },
  widgetItemEdit: {
    padding: theme.old.spacing.unit(2),
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundColor: theme.colors.base.blue[80],
      opacity: 0,
      transition: 'opacity 0.2s',
      pointerEvents: 'none',
      zIndex: 99999,
    },
    '&:hover::before': {
      opacity: 0.8,
    },
    '&:hover .widgetItemActions': {
      zIndex: 99999,
      visibility: 'visible',
      opacity: 1,
    },
  },
  expandedWidgetMask: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  expandedWidget: {
    backgroundColor: 'white',
    padding: 24,
    width: '90%',
    height: '90%',
    overflow: 'auto',
  },
}));

const typeToMinSize = (type: DashboardWidgetType) => {
  switch (type) {
    default: {
      return {
        minW: 3,
        minH: 2,
      };
    }
  }
};

interface DashboardProps {
  className?: string;
  isPojectMember?: boolean;
  projectId?: string;
}

export const Dashboard: React.FC<DashboardProps> = (props) => {
  const linkWidgetDrawerRef =
    useRef<DashboardEditWidgetLinkContentDrawerHandle>(null);
  const powerBIWidgetDrawerRef =
    useRef<DashboardEditWidgetPowerBIContentDrawerHandle>(null);
  //#region ------------------------------ Defaults
  const { className, isPojectMember, projectId } = props;
  const classes = useStyles(props);
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [dashboardItems, setDashboardItems] = useState<DashboardItem[]>([]);
  const [originalDashboardItems, setOriginalDashboardItems] = useState<
    DashboardItem[]
  >([]);
  const project = useSelector<RootReducerState, Project>((state) =>
    getProject(state, projectId)
  );
  const userMe = useSelector(getUserMe);
  const [initDashboardItems, setInitDashboardItems] = useState<DashboardItem[]>(
    []
  );
  const [pendingRemovals, setPendingRemovals] = useState<string[]>([]);

  const [dashboardId, setDashboardId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedDashboardItem, setExpandedDashboardItem] =
    useState<DashboardItem | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const expandedRef = useRef<HTMLDivElement>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isAddDrawerVisible, setIsAddDrawerVisible] = useState(false);
  const [dashboardName, setDashboardName] = useState<string | null>(null);
  const [originalDashboardName, setOriginalDashboardName] = useState<
    string | null
  >(null);
  const [belongsToUserId, setBelongsToUserId] = useState<string | null>(null);
  const [isEditWidgetDrawerVisible, setIsEditWidgetDrawerVisible] =
    useState(false);
  const [dashboardItemToEdit, setDashboardItemToEdit] =
    useState<DashboardItem | null>(null);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);

  const [dashboardItemsToAdd, setDashboardItemsToAdd] = useState<
    DashboardItem[]
  >([]);

  const userMeOfficeRoles = useSelector(getUserMe)?.prioData?.officeRoles;

  const isOfficeHR = useMemo(() => {
    if (!userMeOfficeRoles || typeof userMeOfficeRoles !== 'object')
      return false;
    return Object.values(userMeOfficeRoles)
      .flatMap((roles) => (Array.isArray(roles) ? roles : []))
      .includes('officeHR');
  }, [userMeOfficeRoles]);

  const getAllDashboardItems = useCallback(() => {
    let defaultItems: DashboardItem[] = [];

    // Personal dashboard defaults
    defaultItems = [
      {
        dashboardItemId: uuid(),
        type: 'absenceOverview',
        layout: {
          x: 0,
          y: 0,
          width: 3,
          height: 2,
        },
      },
      {
        dashboardItemId: uuid(),
        type: 'overtimeOverview',
        layout: {
          x: 0,
          y: 2,
          width: 3,
          height: 2,
        },
      },
      {
        dashboardItemId: uuid(),
        type: 'pendingActions',
        layout: {
          x: 3,
          y: 0,
          width: 3,
          height: 2,
        },
      },
      {
        dashboardItemId: uuid(),
        type: 'upcomingAbsences',
        layout: {
          x: 0,
          y: 4,
          width: 3,
          height: 2,
        },
      },
      {
        dashboardItemId: uuid(),
        type: 'upcomingBirthdays',
        layout: {
          x: 3,
          y: 2,
          width: 3,
          height: 2,
        },
      },
      {
        dashboardItemId: uuid(),
        type: 'projectNews',
        layout: {
          x: 6,
          y: 4,
          width: 3,
          height: 2,
        },
      },
      {
        dashboardItemId: uuid(),
        type: 'links',
        layout: {
          x: 3,
          y: 4,
          width: 3,
          height: 2,
        },
      },
      {
        dashboardItemId: uuid(),
        type: 'powerBI',
        layout: {
          x: 3,
          y: 4,
          width: 3,
          height: 2,
        },
      },
    ];

    // Add HR widgets if applicable
    if (isOfficeHR) {
      defaultItems.push({
        dashboardItemId: uuid(),
        type: 'hrTimekeepingOverview',
        layout: {
          x: 6,
          y: 0,
          width: 3,
          height: 2,
        },
      });
    }

    // Add Reteach widget if applicable
    if (ConfigValues.RETEACH_URL) {
      defaultItems.push({
        dashboardItemId: uuid(),
        type: 'reteachOverview',
        layout: {
          x: 6,
          y: 4,
          width: 3,
          height: 2,
        },
      });
    }

    return defaultItems;
  }, [isOfficeHR]);

  const allProjectDashboardItems: DashboardItem[] = useMemo(() => {
    //Add the project-specific dashboard items here
    return [
      {
        dashboardItemId: uuid(),
        type: 'links',
        layout: {
          x: 3,
          y: 0,
          width: 3,
          height: 2,
        },
      },
      {
        dashboardItemId: uuid(),
        type: 'powerBI',
        layout: {
          x: 6,
          y: 0,
          width: 3,
          height: 2,
        },
      },
    ];
  }, []);

  const initialProjectItems: DashboardItem[] = useMemo(
    () => [
      {
        dashboardItemId: uuid(),
        type: 'projectInfos',
        layout: {
          x: 0,
          y: 0,
          width: 3,
          height: 2,
        },
        data: project,
      },
    ],
    [project]
  );

  const availableWidgets = useMemo(() => {
    const defaultItems = projectId
      ? allProjectDashboardItems
      : getAllDashboardItems();

    return defaultItems.filter((item) => {
      const alwaysAvailableTypes = ['links', 'powerBI'];

      if (alwaysAvailableTypes.includes(item.type)) return true;

      return !originalDashboardItems.some(
        (existing) => existing.type === item.type
      );
    });
  }, [
    originalDashboardItems,
    getAllDashboardItems,
    allProjectDashboardItems,
    projectId,
  ]);

  const userIsProjectAdmin = useMemo(() => {
    return isProjectAdmin(project, userMe);
  }, [project, userMe]);

  //#endregion

  //#region ------------------------------ Methods / Handlers
  const fetchDashboard = useCallback(async () => {
    if (projectId && !isPojectMember) {
      setDashboardItems(initialProjectItems);
      return;
    }
    try {
      setIsLoading(true);
      const { data } = await apiFetchDashboard(projectId);
      if (Array.isArray(data) && data.length > 0) {
        const dashboard = data[0];
        setDashboardId(dashboard.dashboardId);
        setDashboardName(dashboard.dashboardName);
        setOriginalDashboardName(dashboard.dashboardName);
        const items = dashboard.widgets.map((widget) => {
          const item = widgetDtoToDashboardItem(widget);
          if (item.type === 'projectInfos') {
            return {
              ...item,
              data: project,
            };
          }
          return item;
        });
        setDashboardItems(items);
        setBelongsToUserId(dashboard.belongsToUserId || null);
        setInitDashboardItems(items);
      } else {
        setDashboardItems(initialProjectItems);
        setInitDashboardItems(initialProjectItems);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      message.error(t('dashboard:errorFetchingDashboard'));
    } finally {
      setIsLoading(false);
    }
  }, [projectId, t, initialProjectItems, isPojectMember, project]);

  const handleRemoveWidget = useCallback((widgetId: string) => {
    setDashboardItems((items) =>
      items.filter((item) => item.dashboardItemId !== widgetId)
    );
    setPendingRemovals((removals) => [...removals, widgetId]);
  }, []);

  const handleExpandDashboardItem = useCallback((widget: DashboardItem) => {
    setExpandedDashboardItem(widget);
  }, []);

  const handleCloseExpandedDashoboardItem = useCallback(() => {
    setExpandedDashboardItem(null);
  }, []);

  const handleLayoutChange = useCallback(
    (newLayout: Layout[]) => {
      if (!isEditMode) return;

      setDashboardItems((prevItems) =>
        prevItems.map((item) => {
          if (pendingRemovals.includes(item.dashboardItemId)) return item;

          const layoutItem = newLayout.find(
            (l) => l.i === item.dashboardItemId
          );
          if (!layoutItem) return item;

          const updatedDashboardItem: DashboardItem = {
            ...item,
            layout: {
              x: layoutItem.x,
              y: layoutItem.y,
              width: layoutItem.w,
              height: layoutItem.h,
            },
          };

          setDashboardItemsToAdd((prev) =>
            prev.map((p) => {
              if (p.dashboardItemId === updatedDashboardItem.dashboardItemId)
                return {
                  ...p,
                  layout: updatedDashboardItem.layout,
                };
              return p;
            })
          );

          return updatedDashboardItem;
        })
      );
    },
    [isEditMode, pendingRemovals]
  );

  const handleResetLayout = useCallback(async () => {
    try {
      setIsLoading(true);
      if (dashboardId) {
        projectId
          ? await apiDeleteProjectDashboard(projectId, dashboardId)
          : await apiDeleteDashboard(dashboardId);
        setDashboardId(null);
        setDashboardItems([]);
        setOriginalDashboardItems([]);
        setPendingRemovals([]);
        fetchDashboard();
        setDashboardName(project?.name + ' Dashboard');
        setOriginalDashboardName(project?.name + ' Dashboard');
      }
      setPendingRemovals([]);

      message.success(t('dashboard:widgets.layoutResetSuscess'));
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to reset layout:', error);
      message.error(t('dashboard:widgets.errorResettingLayout'));
    } finally {
      setIsLoading(false);
    }
  }, [dashboardId, t, fetchDashboard, project?.name, projectId]);

  const handleEditModeToggle = useCallback(() => {
    if (!isEditMode) {
      setOriginalDashboardItems(dashboardItems);
    }
    setIsEditMode(!isEditMode);
  }, [isEditMode, dashboardItems]);

  const handleopenAddWidgetDrawer = useCallback(() => {
    setIsAddDrawerVisible(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDashboardItems(originalDashboardItems);
    setDashboardName(originalDashboardName);
    setDashboardItems((items) =>
      items.filter(
        (item) =>
          !dashboardItemsToAdd.some(
            (widget) => widget.dashboardItemId === item.dashboardItemId
          )
      )
    );
    setInitDashboardItems(originalDashboardItems);
    setOriginalDashboardItems([]);
    setPendingRemovals([]);
    setDashboardItemsToAdd([]);
    setIsEditMode(false);
  }, [originalDashboardItems, dashboardItemsToAdd, originalDashboardName]);

  const handleDefaultGlobalDashboardSettingSave = useCallback(async () => {
    try {
      const dashboardSettingKey = projectId
        ? 'Dashboard_DefaultProjectDashboardConfiguration'
        : 'Dashboard_DefaultDashboardMeConfiguration';
      setIsLoading(true);
      var setting: SettingKey = {
        keys: [dashboardSettingKey],
        scopeId: '',
        eSettingScope: 'global',
      };
      const existingSetting = await apiFetchSettings([setting]);
      const emptyGuid = '00000000-0000-0000-0000-000000000000';
      var settingToSave: DefaultDashboardSetting = {
        dashboard: {
          dashboardType: projectId ? 'project' : 'me',
          belongsToUserId: emptyGuid,
          isFavorite: false,
          isShared: false,
          dashboardName: dashboardName || 'Default Dashboard',
          dashboardDescription: '',
          dashboardId: emptyGuid,
          widgets: dashboardItems.map((item) => dashboardItemToWidgetDto(item)),
        },
      };
      //if empty guid, then it is not existing
      if (
        existingSetting &&
        existingSetting.data &&
        existingSetting.data[0].settingId !== emptyGuid
      ) {
        const settingId = existingSetting.data[0].settingId;
        var updateSetting: UpdateSetting = {
          jsonValue: JSON.stringify(settingToSave),
          parentSettingId: '',
          relationId: '',
          isDefault: true,
          description: 'Default dashboard for all users',
        };
        await apiUpdateSetting(settingId, updateSetting);
      } else {
        var createSetting: UpdateSetting = {
          jsonValue: JSON.stringify(settingToSave),
          parentSettingId: '',
          relationId: '',
          isDefault: true,
          description: 'Default dashboard for all users',
        };
        await apiCreateSetting(createSetting, dashboardSettingKey);
      }
    } catch (error) {
      console.error('Failed to save default global dashboard setting:', error);
      message.error(t('dashboard:widgets.errorSavingDefaultDashboard'));
    }

    setIsLoading(false);
  }, [dashboardItems, dashboardName, projectId, t]);

  const hasLayoutOrDataChanged = useCallback((current, original) => {
    const { layout: curr, data: currData } = current;
    const { layout: orig, data: origData } = original;

    return (
      curr.x !== orig.x ||
      curr.y !== orig.y ||
      curr.width !== orig.width ||
      curr.height !== orig.height ||
      currData !== origData
    );
  }, []);

  const createDeletionUpdates = useCallback(() => {
    return pendingRemovals.map((widgetId) =>
      projectId
        ? apiDeleteProjectDashboardWidget(projectId, dashboardId, widgetId)
        : apiDeleteDashboardWidget(dashboardId, widgetId)
    );
  }, [dashboardId, projectId, pendingRemovals]);

  const createNewWidgetUpdates = useCallback(() => {
    return dashboardItemsToAdd.map((dashboardItem) =>
      projectId
        ? apiCreateProjectDashboardWidget(
            projectId,
            dashboardId,
            dashboardItemToWidgetDto(dashboardItem)
          )
        : apiCreateDashboardWidget(
            dashboardId,
            dashboardItemToWidgetDto(dashboardItem)
          )
    );
  }, [dashboardId, projectId, dashboardItemsToAdd]);

  const createChangeUpdates = useCallback(() => {
    const newWidgetIds = new Set(
      dashboardItemsToAdd.map((w) => w.dashboardItemId)
    );

    const changedItems = dashboardItems.filter((item) => {
      if (newWidgetIds.has(item.dashboardItemId)) return false;

      const original = originalDashboardItems.find(
        (i) => i.dashboardItemId === item.dashboardItemId
      );
      if (!original) return false;

      return hasLayoutOrDataChanged(item, original);
    });

    return changedItems.map((dashboardItem) =>
      projectId
        ? apiUpdateProjectDashboardWidget(
            projectId,
            dashboardId,
            dashboardItem.dashboardItemId,
            dashboardItemToWidgetDto(dashboardItem)
          )
        : apiUpdateDashboardWidget(dashboardId, dashboardItem.dashboardItemId, {
            ...dashboardItemToWidgetDto(dashboardItem),
            dashboardWidgetId: dashboardItem.dashboardItemId,
            dashboardWidgetType: dashboardItem.type as DashboardWidgetType,
          })
    );
  }, [
    dashboardId,
    dashboardItems,
    dashboardItemsToAdd,
    hasLayoutOrDataChanged,
    originalDashboardItems,
    projectId,
  ]);

  const createDashboardNameUpdate = useCallback(() => {
    return apiUpdateProjectDashboard(dashboardId, projectId, {
      dashboardType: 'project',
      dashboardName,
      belongsToUserId,
      isShared: true,
    });
  }, [dashboardId, projectId, dashboardName, belongsToUserId]);

  const processNewDashboardWidgets = useCallback(
    (widgets) => {
      if (projectId) {
        return widgets
          .map(widgetDtoToDashboardItem)
          .map((item) =>
            item.type === 'projectInfos' ? { ...item, data: project } : item
          );
      } else {
        const visibleWidgets = widgets.filter(
          (widget) =>
            widget.dashboardWidgetType !== 'hrTimekeepingOverview' || isOfficeHR
        );
        return visibleWidgets.map(widgetDtoToDashboardItem);
      }
    },
    [projectId, isOfficeHR, project]
  );

  const updateDashboardState = useCallback((newDashboard, processedWidgets) => {
    setDashboardId(newDashboard.dashboardId);
    setDashboardItems(processedWidgets);
    setInitDashboardItems(processedWidgets);
    setOriginalDashboardItems(processedWidgets);
    setDashboardItemsToAdd([]);
    setPendingRemovals([]);
    setDashboardName(newDashboard.dashboardName);
    setOriginalDashboardName(newDashboard.dashboardName);
    if (newDashboard.belongsToUserId !== undefined) {
      setBelongsToUserId(newDashboard.belongsToUserId || null);
    }
  }, []);

  const createNewDashboard = useCallback(async () => {
    const name =
      dashboardName ??
      (projectId ? project?.name + ' Dashboard' : 'My Dashboard');
    const dashboardData = {
      dashboardType: projectId ? 'project' : 'me',
      dashboardName: name,
      ...(projectId && { projectId }),
      widgets: dashboardItems.map(dashboardItemToWidgetDto),
    };

    const { data: newDashboard } = projectId
      ? await apiCreateProjectDashboard(projectId, dashboardData)
      : await apiCreateDashboard(dashboardData);

    if (newDashboard) {
      const processedWidgets = processNewDashboardWidgets(newDashboard.widgets);
      updateDashboardState(newDashboard, processedWidgets);
    }
  }, [
    dashboardName,
    projectId,
    project?.name,
    dashboardItems,
    processNewDashboardWidgets,
    updateDashboardState,
  ]);

  const updateExistingDashboard = useCallback(async () => {
    const updates = [];

    updates.push(...createDeletionUpdates());

    updates.push(...createNewWidgetUpdates());

    updates.push(...createChangeUpdates());

    if (dashboardName !== originalDashboardName) {
      updates.push(createDashboardNameUpdate());
    }

    await Promise.all(updates);
    await fetchDashboard();

    setPendingRemovals([]);
    setDashboardItemsToAdd([]);
    setOriginalDashboardItems(dashboardItems);
    setOriginalDashboardName(dashboardName);
  }, [
    createChangeUpdates,
    createDashboardNameUpdate,
    createDeletionUpdates,
    createNewWidgetUpdates,
    dashboardItems,
    dashboardName,
    fetchDashboard,
    originalDashboardName,
  ]);

  const handleSaveLayout = useCallback(async () => {
    try {
      setIsLoading(true);

      const isNewDashboard =
        !dashboardId || dashboardId === '00000000-0000-0000-0000-000000000000';

      if (isNewDashboard) {
        await createNewDashboard();
      } else {
        await updateExistingDashboard();
      }

      message.success(t('dashboard:widgets.layoutSaveSuccess'));
      setIsEditMode(false);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save layout:', error);
      message.error(t('dashboard:widgets.errorSavingLayout'));
    } finally {
      setIsLoading(false);
    }
  }, [t, dashboardId, updateExistingDashboard, createNewDashboard]);

  const checkForChanges = useCallback(() => {
    const removalsExist = pendingRemovals.length > 0;

    const itemsChanged = dashboardItems.some((item) => {
      const original = originalDashboardItems.find(
        (o) => o.dashboardItemId === item.dashboardItemId
      );
      if (!original) return true;

      const { layout: currLayout, data: currData } = item;
      const { layout: origLayout, data: origData } = original;

      return (
        currLayout.x !== origLayout.x ||
        currLayout.y !== origLayout.y ||
        currLayout.width !== origLayout.width ||
        currLayout.height !== origLayout.height ||
        currData !== origData
      );
    });

    const newItemsExist = dashboardItems.some(
      (item) =>
        !originalDashboardItems.find(
          (o) => o.dashboardItemId === item.dashboardItemId
        )
    );
    const originalDashboardNameChanged =
      dashboardName !== originalDashboardName && dashboardName !== '';

    return (
      removalsExist ||
      itemsChanged ||
      newItemsExist ||
      originalDashboardNameChanged
    );
  }, [
    dashboardItems,
    originalDashboardItems,
    pendingRemovals,
    dashboardName,
    originalDashboardName,
  ]);

  const handleWidgetAdded = useCallback((newWidget: DashboardItem) => {
    const widgetWithId = {
      ...newWidget,
      dashboardItemId: uuid(),
      layout: {
        x: 0,
        y: 0,
        width: 3,
        height: 2,
      },
    };
    setDashboardItems((prevItems) => [...prevItems, widgetWithId]);
    setOriginalDashboardItems((prevOriginal) => [
      ...prevOriginal,
      widgetWithId,
    ]);
    setDashboardItemsToAdd((prevWidgets) => [...prevWidgets, widgetWithId]);
  }, []);

  const handleSaveWidgetChanges = useCallback(async () => {
    const widgetRef =
      dashboardItemToEdit?.type === 'links'
        ? linkWidgetDrawerRef
        : dashboardItemToEdit?.type === 'powerBI'
          ? powerBIWidgetDrawerRef
          : null;

    if (dashboardItemToEdit && widgetRef?.current) {
      const data = widgetRef.current.getData();

      const updatedDashboardItems = dashboardItems.map((item) =>
        item.dashboardItemId === dashboardItemToEdit.dashboardItemId
          ? { ...item, data }
          : item
      );

      setDashboardItems(updatedDashboardItems);
      setDashboardItemsToAdd((prev) =>
        prev.map((p) => {
          const addedUpdatedWidget = updatedDashboardItems.find(
            (di) => di.dashboardItemId === p.dashboardItemId
          );
          return addedUpdatedWidget
            ? { ...p, data: addedUpdatedWidget.data }
            : p;
        })
      );
      setInitDashboardItems(updatedDashboardItems);
      setIsEditWidgetDrawerVisible(false);
      setDashboardItemToEdit(null);
    }
  }, [dashboardItems, dashboardItemToEdit]);

  const handleCancelWidgetChanges = useCallback(() => {
    setIsEditWidgetDrawerVisible(false);
    setDashboardItemToEdit(null);
    setIsSubmitButtonDisabled(true);
  }, []);
  //#endregion

  //#region ------------------------------ Effects

  useEffect(
    () => {
      fetchDashboard();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (expandedDashboardItem && expandedRef.current) {
      expandedRef.current.focus();
    }
  }, [expandedDashboardItem]);

  useEffect(() => {
    setHasChanges(checkForChanges());
  }, [checkForChanges]);
  //#endregion

  //#region ------------------------------ Render
  const renderWidgetActions = useCallback(
    (item: DashboardItem) => {
      if (!isEditMode) return null;

      return (
        <div className={`${classes.widgetItemActions} widgetItemActions`}>
          <WidgetActions
            item={item}
            onExpand={handleExpandDashboardItem}
            onRemove={handleRemoveWidget}
            onEdit={() => {
              setIsEditWidgetDrawerVisible(true);
              setDashboardItemToEdit(item);
            }}
            isExpandable={
              item.type === 'absenceOverview' ||
              item.type === 'reteachOverview' ||
              item.type === 'powerBI' ||
              item.type === 'projectInfos'
            }
            isEditable={item.type === 'links' || item.type === 'powerBI'}
            isNotDeletable={item.type === 'projectInfos'}
          />
        </div>
      );
    },
    [
      classes.widgetItemActions,
      handleExpandDashboardItem,
      handleRemoveWidget,
      isEditMode,
    ]
  );

  const renderEditDrawerContent = useCallback(() => {
    if (!dashboardItemToEdit) return null;

    switch (dashboardItemToEdit.type) {
      case 'links':
        return (
          <DashboardEditWidgetLinkContentDrawer
            ref={linkWidgetDrawerRef}
            onValidityChange={(isValid) => setIsSubmitButtonDisabled(!isValid)}
            dashboardItem={dashboardItemToEdit}
          />
        );
      case 'powerBI':
        return (
          <DashboardEditWidgetPowerBIContentDrawer
            ref={powerBIWidgetDrawerRef}
            onValidityChange={(isValid) => setIsSubmitButtonDisabled(!isValid)}
            dashboardItem={dashboardItemToEdit}
          />
        );

      default:
        return null;
    }
  }, [dashboardItemToEdit]);
  //#endregion

  return (
    <>
      {(!projectId || isPojectMember) && (
        <DashboardActionsBar
          isEditMode={isEditMode}
          isLoading={isLoading}
          onToggleEditMode={handleEditModeToggle}
          onSaveLayout={handleSaveLayout}
          onDefaultGlobalDashboardSettingSave={
            handleDefaultGlobalDashboardSettingSave
          }
          onResetLayout={handleResetLayout}
          onCancelChanges={handleCancel}
          hasChanges={hasChanges}
          onOpenAddWidgetDrawer={handleopenAddWidgetDrawer}
          projectId={projectId}
          dashboardName={dashboardName}
          setDashboardName={setDashboardName}
          isProjectAdmin={userIsProjectAdmin}
        />
      )}

      <DashboardContext.Provider
        value={{
          initDashboardItems,
          dashboardItems,
          setDashboardItems,
          expandDashboardItem: handleExpandDashboardItem,
          closeExpandedDashboardItem: handleCloseExpandedDashoboardItem,
          expandedDashboardItem,
        }}
      >
        <div className={classNames(classes.root, className)}>
          <GridLayout
            className="layout"
            isDraggable={isEditMode}
            isResizable={isEditMode}
            onLayoutChange={handleLayoutChange}
            draggableCancel=".widgetItemActions, button"
            layout={dashboardItems?.map<Layout>(
              ({ dashboardItemId, layout: { x, y, width, height }, type }) => ({
                x,
                y,
                w: width,
                h: height,
                i: dashboardItemId,
                ...typeToMinSize(type),
              })
            )}
            cols={12}
            width={1920}
            rowHeight={160}
          >
            {dashboardItems.map(({ dashboardItemId, type, data, layout }) => (
              <div
                key={dashboardItemId}
                className={classNames(
                  isEditMode ? classes.widgetItemEdit : classes.widgetItem
                )}
              >
                {renderWidgetActions({ dashboardItemId, type, data, layout })}
                <div
                  style={{
                    pointerEvents: isEditMode ? 'none' : 'auto',
                    inset: 0,
                    position: 'absolute',
                    padding: '16px',
                  }}
                >
                  <DashboardBaseItem
                    dashboardItemId={dashboardItemId}
                    type={type}
                    data={data}
                  />
                </div>
              </div>
            ))}
          </GridLayout>
          {expandedDashboardItem && (
            <div
              className={classes.expandedWidgetMask}
              onClick={handleCloseExpandedDashoboardItem}
            >
              <div
                ref={expandedRef}
                className={classes.expandedWidget}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    handleCloseExpandedDashoboardItem();
                  }
                }}
                tabIndex={0}
              >
                <DashboardBaseItem
                  dashboardItemId={expandedDashboardItem.dashboardItemId}
                  type={expandedDashboardItem.type}
                  data={expandedDashboardItem.data}
                />
              </div>
            </div>
          )}
          <Drawer
            title={t('dashboard:widgets.addWidgetDrawer.title')}
            visible={isEditMode && isAddDrawerVisible}
            onClose={() => setIsAddDrawerVisible(false)}
            closable
            width="large"
          >
            {availableWidgets.length > 0 ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '16px',
                }}
              >
                {availableWidgets.map((item) => (
                  <WidgetCard
                    key={item.dashboardItemId}
                    item={item}
                    onAdd={handleWidgetAdded}
                  />
                ))}
              </div>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '16px',
                  alignContent: 'center',
                  height: '100%',
                }}
              >
                {t('dashboard:widgets.addWidgetDrawer.noItems')}
              </div>
            )}
          </Drawer>
          <Drawer
            title={t('dashboard:widgets.editWidgetItemDrawer.title', {
              type:
                dashboardItemToEdit && dashboardItemToEdit.type
                  ? t(`dashboard:widgets.title.${dashboardItemToEdit.type}`)
                  : '',
            })}
            visible={isEditMode && isEditWidgetDrawerVisible}
            onClose={handleCancelWidgetChanges}
            closable
            footer={
              <Flex.Row
                justifyContent="flex-end"
                childrenGap={theme.spacing.small}
              >
                <Button type="default" onClick={handleCancelWidgetChanges}>
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  disabled={isSubmitButtonDisabled}
                  onClick={handleSaveWidgetChanges}
                >
                  {t('common:actions.apply')}
                </Button>
              </Flex.Row>
            }
          >
            {renderEditDrawerContent()}
          </Drawer>
        </div>
      </DashboardContext.Provider>
    </>
  );
};

export default Dashboard;
